// src/components/SignoffHistoryView.jsx
//
// Renders the chronological audit trail of signoff actions on a single
// section. Each entry in `signoffHistory` is shaped:
//   { action: 'signed' | 'undone', by: uid, byName: string, at: timestamp,
//     notes?: string, reason?: string }
//
// Used inline in CheckoutStudentDetail under each section. Collapsed by
// default; clicking the header toggles open.

import React, { useState } from 'react';
import { formatSignoffTimestamp, KA_COLORS } from '../utils/checkoutHelpers';

const styles = {
  wrapper: { marginTop: 8 },
  toggle: {
    background: 'none',
    border: 'none',
    color: KA_COLORS.navy,
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 0',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  },
  list: {
    listStyle: 'none',
    margin: '6px 0 0',
    padding: 8,
    background: KA_COLORS.lightGray,
    borderRadius: 4,
    border: `1px solid ${KA_COLORS.borderGray}`,
    fontSize: 12,
    color: '#333',
  },
  empty: {
    fontStyle: 'italic',
    color: '#999',
    margin: 0,
  },
  entry: (isUndo) => ({
    display: 'flex',
    gap: 8,
    padding: '6px 0',
    borderBottom: `1px dashed ${KA_COLORS.borderGray}`,
    alignItems: 'flex-start',
    opacity: isUndo ? 0.75 : 1,
  }),
  entryLast: { borderBottom: 'none' },
  bullet: (isUndo) => ({
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: isUndo ? '#999' : KA_COLORS.gold,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  }),
  body: { flex: 1, lineHeight: 1.5 },
  byName: { fontWeight: 600, color: KA_COLORS.navy },
  meta: { color: '#666', fontSize: 11 },
  notes: {
    margin: '3px 0 0',
    padding: '4px 6px',
    background: '#fff',
    borderRadius: 3,
    border: `1px solid ${KA_COLORS.borderGray}`,
    fontSize: 11,
    color: '#444',
    fontStyle: 'italic',
  },
};

const SignoffHistoryView = ({ history, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const entries = Array.isArray(history) ? history : [];

  return (
    <div style={styles.wrapper}>
      <button
        type="button"
        style={styles.toggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? '▼' : '▶'} {entries.length} signoff event
        {entries.length === 1 ? '' : 's'}
      </button>

      {open && (
        <ul style={styles.list}>
          {entries.length === 0 ? (
            <li style={styles.empty}>No signoff actions recorded.</li>
          ) : (
            entries.map((entry, idx) => {
              const isUndo = entry.action === 'undone';
              const isLast = idx === entries.length - 1;
              return (
                <li
                  key={`${entry.at?.seconds ?? idx}-${entry.action}`}
                  style={{
                    ...styles.entry(isUndo),
                    ...(isLast ? styles.entryLast : {}),
                  }}
                >
                  <div style={styles.bullet(isUndo)}>
                    {isUndo ? '↺' : '✓'}
                  </div>
                  <div style={styles.body}>
                    <div>
                      <span style={styles.byName}>
                        {entry.byName || 'Unknown user'}
                      </span>
                      {' '}
                      {isUndo ? 'undid signoff' : 'signed off'}
                    </div>
                    <div style={styles.meta}>
                      {formatSignoffTimestamp(entry.at)}
                    </div>
                    {entry.notes && entry.notes.trim().length > 0 && (
                      <p style={styles.notes}>
                        <strong>Notes:</strong> {entry.notes}
                      </p>
                    )}
                    {entry.reason && entry.reason.trim().length > 0 && (
                      <p style={styles.notes}>
                        <strong>Reason for undo:</strong> {entry.reason}
                      </p>
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default SignoffHistoryView;